import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { ArrowRight, ChevronRight, Clock } from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { JsonLd, faqPageJsonLd, breadcrumbJsonLd } from '@/components/JsonLd';
import { buttonVariants } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';
import {
  SITE_ORIGIN,
  SITE_LOCALE,
  HREFLANG_TAGS,
  LOCALE_ORIGINS,
  ogImageUrlForPath,
} from '@/lib/site-config';
import { getGuide, guideAlternates, type GuideMeta } from '@/data/guides-data';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { Nis2LoggingGuideBody } from '@/pages/guides/Nis2LoggingGuide';
import { Nis2CyberbeveiligingswetGuideBody } from '@/pages/guides/Nis2CyberbeveiligingswetGuide';
import { Nis2UmsetzungsgesetzGuideBody } from '@/pages/guides/Nis2UmsetzungsgesetzGuide';
import { Nis2NetherlandsGuideBody } from '@/pages/guides/Nis2NetherlandsGuide';
import { MeldplichtCbwGuideBody } from '@/pages/guides/MeldplichtCbwGuide';
import { UitgebuiteCveGuideBody } from '@/pages/guides/UitgebuiteCveGuide';
import { AiAgentsWebwinkelsGuideBody } from '@/pages/guides/AiAgentsWebwinkelsGuide';
import { InlogLekkenGuideBody } from '@/pages/guides/InlogLekkenGuide';
import { Nis2FranceGuideBody } from '@/pages/guides/Nis2FranceGuide';
import { Nis2EspanaGuideBody } from '@/pages/guides/Nis2EspanaGuide';
import { Nis2ItaliaGuideBody } from '@/pages/guides/Nis2ItaliaGuide';
import { DoraLoggingGuideBody } from '@/pages/guides/DoraLoggingGuide';
import { LogRetentionGuideBody } from '@/pages/guides/LogRetentionGuide';
import { RiskBasedAlertingGuideBody } from '@/pages/guides/RiskBasedAlertingGuide';
import { AgenticSocGuideBody } from '@/pages/guides/AgenticSocGuide';
import { McpForSecurityGuideBody } from '@/pages/guides/McpForSecurityGuide';
import { WhatIsSiemGuideBody } from '@/pages/guides/WhatIsSiemGuide';
import { WhatIsUebaGuideBody } from '@/pages/guides/WhatIsUebaGuide';
import { WhatIsSoarGuideBody } from '@/pages/guides/WhatIsSoarGuide';
import { DetectionAsCodeGuideBody } from '@/pages/guides/DetectionAsCodeGuide';
import { WhatIsLogManagementGuideBody } from '@/pages/guides/WhatIsLogManagementGuide';
import { SiemVsLogManagementVsXdrGuideBody } from '@/pages/guides/SiemVsLogManagementVsXdrGuide';
import { ClickhouseVsElasticsearchGuideBody } from '@/pages/guides/ClickhouseVsElasticsearchGuide';
import { LpqlVsSplGuideBody } from '@/pages/guides/LpqlVsSplGuide';
import { SplunkItsiGuideBody } from '@/pages/guides/SplunkItsiGuide';
import { SplunkItsiUitgelegdGuideBody } from '@/pages/guides/SplunkItsiUitgelegdGuide';
import { StructuredLoggingGuideBody } from '@/pages/guides/StructuredLoggingGuide';
import { Iso27001LoggingGuideBody } from '@/pages/guides/Iso27001LoggingGuide';
import { GdprPiiInLogsGuideBody } from '@/pages/guides/GdprPiiInLogsGuide';
import { WhatIsMitreAttackGuideBody } from '@/pages/guides/WhatIsMitreAttackGuide';
import { AiSocAnalystGuideBody } from '@/pages/guides/AiSocAnalystGuide';
import { AiRootCauseAnalysisGuideBody } from '@/pages/guides/AiRootCauseAnalysisGuide';
import { LogCollectionAgentGuideBody } from '@/pages/guides/LogCollectionAgentGuide';
import { ProductionLogsAiIdeGuideBody } from '@/pages/guides/ProductionLogsAiIdeGuide';
import { ProductielogsAiIdeGuideBody } from '@/pages/guides/ProductielogsAiIdeGuide';

/**
 * Slug → article body. Each guide's prose lives in its own component under
 * pages/guides/. Bodies are imported eagerly here; with a handful of guides
 * that is fine (they share the GuidePage chunk). Switch to React.lazy per body
 * if this list grows large.
 */
const GUIDE_BODIES: Record<string, () => ReactNode> = {
  'nis2-logging-requirements': Nis2LoggingGuideBody,
  'nis2-cyberbeveiligingswet': Nis2CyberbeveiligingswetGuideBody,
  'nis2-umsetzungsgesetz': Nis2UmsetzungsgesetzGuideBody,
  'nis2-netherlands-cyberbeveiligingswet': Nis2NetherlandsGuideBody,
  'meldplicht-cyberbeveiligingswet': MeldplichtCbwGuideBody,
  'uitgebuite-cve-welke-raken-jou': UitgebuiteCveGuideBody,
  'ai-agents-webwinkels-aanvallen': AiAgentsWebwinkelsGuideBody,
  'datalek-via-inlog-herkennen': InlogLekkenGuideBody,
  'nis2-france-loi-resilience': Nis2FranceGuideBody,
  'nis2-espana-ley-ciberseguridad': Nis2EspanaGuideBody,
  'nis2-italia-dlgs-138': Nis2ItaliaGuideBody,
  'dora-logging-monitoring-requirements': DoraLoggingGuideBody,
  'log-retention-requirements': LogRetentionGuideBody,
  'what-is-risk-based-alerting': RiskBasedAlertingGuideBody,
  'what-is-an-agentic-soc': AgenticSocGuideBody,
  'mcp-for-security': McpForSecurityGuideBody,
  'production-logs-in-your-ai-ide': ProductionLogsAiIdeGuideBody,
  'productielogs-in-je-ai-ide': ProductielogsAiIdeGuideBody,
  'what-is-siem': WhatIsSiemGuideBody,
  'what-is-ueba': WhatIsUebaGuideBody,
  'what-is-soar': WhatIsSoarGuideBody,
  'detection-as-code': DetectionAsCodeGuideBody,
  'what-is-log-management': WhatIsLogManagementGuideBody,
  'siem-vs-log-management-vs-xdr': SiemVsLogManagementVsXdrGuideBody,
  'clickhouse-vs-elasticsearch-logs': ClickhouseVsElasticsearchGuideBody,
  'lpql-vs-spl': LpqlVsSplGuideBody,
  'splunk-itsi-explained': SplunkItsiGuideBody,
  'splunk-itsi-uitgelegd': SplunkItsiUitgelegdGuideBody,
  'structured-logging': StructuredLoggingGuideBody,
  'iso-27001-logging-requirements': Iso27001LoggingGuideBody,
  'gdpr-pii-in-logs': GdprPiiInLogsGuideBody,
  'what-is-mitre-attack': WhatIsMitreAttackGuideBody,
  'ai-soc-analyst': AiSocAnalystGuideBody,
  'ai-root-cause-analysis': AiRootCauseAnalysisGuideBody,
  'what-is-a-log-collection-agent': LogCollectionAgentGuideBody,
};

const DATE_LOCALE: Record<string, string> = {
  en: 'en-US',
  nl: 'nl-NL',
  de: 'de-DE',
  fr: 'fr-FR',
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(DATE_LOCALE[SITE_LOCALE] ?? 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${iso}T00:00:00Z`));
}

function articleJsonLd(meta: GuideMeta) {
  const url = `${SITE_ORIGIN}/guides/${meta.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: meta.title,
    description: meta.excerpt,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    datePublished: `${meta.datePublished}T00:00:00Z`,
    dateModified: `${meta.dateModified}T00:00:00Z`,
    articleSection: meta.category,
    isAccessibleForFree: true,
    image: ogImageUrlForPath(`/guides/${meta.slug}`),
    author: { '@type': 'Organization', name: 'LogPulse', url: SITE_ORIGIN },
    publisher: {
      '@type': 'Organization',
      name: 'LogPulse',
      url: SITE_ORIGIN,
      logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/favicon.svg` },
    },
  };
}

export function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const t = useT();
  const meta = slug ? getGuide(slug, SITE_LOCALE) : undefined;
  const Body = slug ? GUIDE_BODIES[slug] : undefined;

  // Hooks must run unconditionally. Feed safe fallbacks when the slug is unknown.
  useDocumentMeta({
    title: meta?.metaTitle ?? 'Guide not found | LogPulse',
    description: meta?.description ?? '',
  });

  // Cross-language hreflang: guides live on different domains with different
  // slugs, so the standard same-path marketing hreflang does not apply (see
  // isMarketingPath). useDocumentMeta cleared any alternates above; append
  // this guide's language versions (self + altOf counterparts).
  useEffect(() => {
    if (!meta) return;
    const alternates = guideAlternates(meta);
    if (alternates.length < 2) return;
    const links: HTMLLinkElement[] = [];
    const append = (hreflang: string, href: string) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      document.head.appendChild(link);
      links.push(link);
    };
    for (const alt of alternates) {
      append(HREFLANG_TAGS[alt.locale], `${LOCALE_ORIGINS[alt.locale]}/guides/${alt.slug}`);
    }
    const en = alternates.find((a) => a.locale === 'en');
    if (en) append('x-default', `${LOCALE_ORIGINS.en}/guides/${en.slug}`);
    return () => links.forEach((l) => l.remove());
  }, [meta]);

  if (!meta || !Body) {
    return <NotFoundPage />;
  }

  const pageUrl = `${SITE_ORIGIN}/guides/${meta.slug}`;
  const breadcrumb = breadcrumbJsonLd([
    { name: 'Home', url: SITE_ORIGIN },
    { name: 'Guides', url: `${SITE_ORIGIN}/guides` },
    { name: meta.title, url: pageUrl },
  ]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <JsonLd data={articleJsonLd(meta)} />
      <JsonLd data={breadcrumb} />
      <JsonLd data={faqPageJsonLd(meta.faq, HREFLANG_TAGS[SITE_LOCALE])} />
      <LandingHeader />

      {/* Header */}
      <section className="relative pt-32 pb-10 sm:pt-40 sm:pb-12 gradient-mesh overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-500">
            <LocalizedAnchor href="/guides" className="hover:text-gray-900 dark:hover:text-gray-300">
              {t('guidePage.breadcrumb')}
            </LocalizedAnchor>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-gray-700 dark:text-gray-400">{meta.category}</span>
          </nav>

          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-balance">
            <span className="text-gradient">{meta.title}</span>
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {meta.readTime} {t('guidePage.read')}
            </span>
            <span aria-hidden>·</span>
            <span>{t('guidePage.updated')} {formatDate(meta.dateModified)}</span>
          </div>
        </div>
      </section>

      {/* Article body */}
      <article className="pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Body />
        </div>
      </article>

      {/* FAQ: visible (mirrors the FAQPage JSON-LD above) */}
      <section className="py-14 sm:py-16 border-t border-gray-200/60 dark:border-gray-800/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-8 text-balance">
            {t('guidePage.faqTitle')}
          </h2>
          <dl className="space-y-8">
            {meta.faq.map((item) => (
              <div key={item.q}>
                <dt className="text-lg font-semibold text-gray-900 dark:text-white">{item.q}</dt>
                <dd className="mt-2 text-gray-600 dark:text-gray-400 leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Related links: contextual internal links (hub-and-spoke) */}
      {meta.related.length > 0 && (
        <section className="pb-6">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 text-sm">
              <span className="font-semibold text-gray-900 dark:text-white">{t('guidePage.related')}</span>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {meta.related.map((r) => (
                  <LocalizedAnchor
                    key={r.href}
                    href={r.href}
                    className="text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                  >
                    {r.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </LocalizedAnchor>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 sm:py-28 gradient-subtle">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 text-balance">
            {t('guidePage.cta.title')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 text-balance">
            {t('guidePage.cta.body')}
          </p>
          <a href="https://app.logpulse.io/sign-up" className={buttonVariants({ variant: 'gradient', size: '2xl' })}>
            {t('guidePage.cta.button')}
            <ArrowRight className="h-5 w-5 ml-1" />
          </a>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
